import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import type { Database } from "@/types/database.types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";

type ProjectRow = Database["public"]["Tables"]["projects"]["Row"];
type ProjectMemberRow = Database["public"]["Views"]["project_members_with_profiles"]["Row"];
type MemberRole = Database["public"]["Enums"]["member_role"];

export function ProjectSettings() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [project, setProject] = useState<ProjectRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [hourlyRate, setHourlyRate] = useState<number>(50);
  const [currency, setCurrency] = useState<string>("USD");
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<ProjectMemberRow[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [addUsername, setAddUsername] = useState("");
  const [addRole, setAddRole] = useState<Database["public"]["Enums"]["member_role"]>("viewer");
  const [adding, setAdding] = useState(false);
  const [invites, setInvites] = useState<
    Array<{
      id: string;
      token: string;
      role: Database["public"]["Enums"]["member_role"];
      expires_at: string | null;
      created_at: string | null;
    }>
  >([]);
  const [invitesLoading, setInvitesLoading] = useState(true);

  const [currentUserRole, setCurrentUserRole] = useState<MemberRole | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!id || !user) return;
    let isMounted = true;

    // Fetch project and verify access
    const fetchProject = async () => {
      const { data: projectData, error: projectError } = await supabase
        .from("projects")
        .select("*")
        .eq("id", id)
        .single();

      if (!isMounted) return;

      if (projectError) {
        setError(projectError.message);
        setLoading(false);
        return;
      }

      // Check role
      const { data: memberData } = await supabase
        .from("project_members")
        .select("role")
        .eq("project_id", id)
        .eq("user_id", user.id)
        .maybeSingle();

      const role = memberData?.role;
      const isProjectOwner = projectData?.owner_id === user.id;

      if (role !== "owner" && !isProjectOwner) {
        navigate(`/project/${id}`);
        return;
      }

      // If user is project owner but has no role, consider them owner
      setCurrentUserRole(role || (isProjectOwner ? "owner" : null));
      setProject(projectData);
      setName(projectData?.name ?? "");
      setIsPublic(Boolean(projectData?.is_public));
      setHourlyRate(projectData?.hourly_rate ? parseFloat(projectData.hourly_rate.toString()) : 50);
      setCurrency((projectData as any)?.currency || "USD");

      setLoading(false);
    };

    fetchProject();

    return () => {
      isMounted = false;
    };
  }, [id, navigate, user]);

  useEffect(() => {
    if (!id || !currentUserRole) return; // Wait for role check
    setMembersLoading(true);
    supabase
      .from("project_members_with_profiles")
      .select("*")
      .eq("project_id", id)
      .order("role", { ascending: false }) // Owner first
      .order("added_at", { ascending: true })
      .then(({ data, error }) => {
        if (error) {
          setError(error instanceof Error ? error.message : "Failed to load members");
        } else {
          setMembers((data as ProjectMemberRow[]) || []);
        }
        setMembersLoading(false);
      });

    setInvitesLoading(true);
    supabase
      .from("project_invites")
      .select("id, token, role, expires_at, created_at")
      .eq("project_id", id)
      .gt("expires_at", new Date().toISOString())
      .then(({ data, error }) => {
        if (error) {
          console.error("Error loading invites:", error);
        } else {
          setInvites(data ?? []);
        }
        setInvitesLoading(false);
      });
  }, [id, currentUserRole]);

  async function refreshMembers() {
    if (!id) return;
    setMembersLoading(true);
    const { data, error } = await supabase
      .from("project_members_with_profiles")
      .select("*")
      .eq("project_id", id)
      .order("role", { ascending: false }) // Owner first
      .order("added_at", { ascending: true });
    if (!error) {
      setMembers((data as ProjectMemberRow[]) || []);
    }
    setMembersLoading(false);
  }

  async function handleAddMember() {
    if (!id || !addUsername.trim() || !user) return;
    setAdding(true);
    setError(null);
    try {
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id, username")
        .eq("username", addUsername.trim())
        .single();

      if (profileError && profileError.code === "PGRST116") {
        const token = crypto.randomUUID();
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 7);
        const { error: inviteError } = await supabase.from("project_invites").insert({
          project_id: id,
          inviter_id: user.id,
          token,
          role: addRole,
          expires_at: expiresAt.toISOString(),
        });
        if (inviteError) throw inviteError;
        const inviteUrl = `${window.location.origin}/invite/${token}`;
        setError(
          `User "${addUsername}" not registered yet. Invite link created and copied to clipboard! Share: ${inviteUrl}`,
        );
        await navigator.clipboard.writeText(inviteUrl);
        await refreshInvites();
      } else if (profileError) {
        throw profileError;
      } else {
        const { error: insertError } = await supabase
          .from("project_members")
          .insert({ project_id: id, user_id: profile!.id, role: addRole });
        if (insertError) throw insertError;
        await refreshMembers();
      }
      setAddUsername("");
      setAddRole("viewer");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add member");
    } finally {
      setAdding(false);
    }
  }

  async function refreshInvites() {
    if (!id) return;
    setInvitesLoading(true);
    const { data, error } = await supabase
      .from("project_invites")
      .select("id, token, role, expires_at, created_at")
      .eq("project_id", id)
      .gt("expires_at", new Date().toISOString());
    if (error) {
      console.error("Error loading invites:", error);
    } else {
      setInvites(data ?? []);
    }
    setInvitesLoading(false);
  }

  async function handleDeleteInvite(inviteId: string) {
    if (!id) return;
    const { error } = await supabase.from("project_invites").delete().eq("id", inviteId);
    if (!error) {
      await refreshInvites();
    }
  }

  function copyInviteLink(token: string) {
    const inviteUrl = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(inviteUrl);
  }

  async function handleRoleChange(userId: string, role: MemberRole) {
    if (!id) return;
    const { error } = await supabase
      .from("project_members")
      .update({ role })
      .eq("project_id", id)
      .eq("user_id", userId);
    if (!error) {
      setMembers((prev) => prev.map((m) => (m.user_id === userId ? { ...m, role } : m)));
    }
  }

  async function handleRemove(userId: string) {
    if (!id) return;
    const { error } = await supabase
      .from("project_members")
      .delete()
      .eq("project_id", id)
      .eq("user_id", userId);
    if (!error) {
      setMembers((prev) => prev.filter((m) => m.user_id !== userId));
    }
  }

  async function save() {
    if (!id) return;
    setSaving(true);
    setError(null);
    const { error } = await supabase
      .from("projects")
      .update({
        name,
        is_public: isPublic,
        hourly_rate: hourlyRate,
        currency: currency,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);
    if (error) setError(error.message);
    setSaving(false);
  }

  if (loading) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <header className="border-b bg-background p-4 md:p-6 flex items-center justify-between gap-4 shrink-0 min-w-0 ">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold truncate">Loading...</h1>
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="text-muted-foreground">Loading…</div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <header className="border-b bg-background p-4 md:p-6 flex items-center justify-between gap-4 shrink-0 min-w-0 ">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold truncate">Error</h1>
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <div className="text-destructive">{error || "Project not found"}</div>
            <Button onClick={() => navigate(`/project/${id}`)}>Back to Project</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      <header className="border-b bg-background p-4 md:p-6 flex items-center justify-between gap-4 shrink-0 min-w-0 ">
        <h1 className="text-xl md:text-2xl lg:text-3xl font-bold truncate">Project Settings</h1>
      </header>
      <main className={`flex-1 overflow-y-auto p-6 ${isMobile ? "pb-content-safe" : ""}`}>
        <div className="w-full space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>General</CardTitle>
              <CardDescription>Update the basic project information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Project name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={isPublic} onCheckedChange={(v) => setIsPublic(Boolean(v))} />
                <Label className="mt-0!">Make project public</Label>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="currency">Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger id="currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD - US Dollar ($)</SelectItem>
                    <SelectItem value="EUR">EUR - Euro (€)</SelectItem>
                    <SelectItem value="GBP">GBP - British Pound (£)</SelectItem>
                    <SelectItem value="JPY">JPY - Japanese Yen (¥)</SelectItem>
                    <SelectItem value="AUD">AUD - Australian Dollar (A$)</SelectItem>
                    <SelectItem value="CAD">CAD - Canadian Dollar (C$)</SelectItem>
                    <SelectItem value="CHF">CHF - Swiss Franc (CHF)</SelectItem>
                    <SelectItem value="CNY">CNY - Chinese Yuan (¥)</SelectItem>
                    <SelectItem value="INR">INR - Indian Rupee (₹)</SelectItem>
                    <SelectItem value="IDR">IDR - Indonesian Rupiah (Rp)</SelectItem>
                    <SelectItem value="SGD">SGD - Singapore Dollar (S$)</SelectItem>
                    <SelectItem value="HKD">HKD - Hong Kong Dollar (HK$)</SelectItem>
                    <SelectItem value="NZD">NZD - New Zealand Dollar (NZ$)</SelectItem>
                    <SelectItem value="KRW">KRW - South Korean Won (₩)</SelectItem>
                    <SelectItem value="MXN">MXN - Mexican Peso (MX$)</SelectItem>
                    <SelectItem value="BRL">BRL - Brazilian Real (R$)</SelectItem>
                    <SelectItem value="ZAR">ZAR - South African Rand (R)</SelectItem>
                    <SelectItem value="SEK">SEK - Swedish Krona (kr)</SelectItem>
                    <SelectItem value="NOK">NOK - Norwegian Krone (kr)</SelectItem>
                    <SelectItem value="DKK">DKK - Danish Krone (kr)</SelectItem>
                    <SelectItem value="PLN">PLN - Polish Złoty (zł)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="hourly-rate">Hourly Rate</Label>
                <Input
                  id="hourly-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
                  placeholder="50.00"
                />
                <p className="text-xs text-muted-foreground">
                  Default billing rate for billable time entries in this project
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={save} disabled={saving}>
                  {saving ? "Saving…" : "Save changes"}
                </Button>
                <Button variant="outline" onClick={() => navigate(`/project/${id}`)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Members</CardTitle>
              <CardDescription>Manage who can access this project</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="grid gap-1 flex-1">
                  <Label htmlFor="username">Add by username</Label>
                  <Input
                    id="username"
                    placeholder="jane_doe"
                    value={addUsername}
                    onChange={(e) => setAddUsername(e.target.value)}
                  />
                </div>
                <div className="grid gap-1 w-full sm:w-48">
                  <Label>Role</Label>
                  <Select
                    value={addRole}
                    onValueChange={(v) =>
                      setAddRole(v as Database["public"]["Enums"]["member_role"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="commenter">Commenter</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="self-end">
                  <Button onClick={handleAddMember} disabled={adding}>
                    {adding ? "Adding…" : "Add member"}
                  </Button>
                </div>
              </div>

              {membersLoading ? (
                <div className="text-sm text-muted-foreground">Loading members…</div>
              ) : (
                <div className="divide-y rounded-md border">
                  {members.length === 0 && (
                    <div className="p-3 text-sm text-muted-foreground">No members yet.</div>
                  )}
                  {members.map((m) => (
                    <div key={m.user_id} className="flex items-center justify-between gap-3 p-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">
                          {m.full_name || m.username || m.user_id}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          @{m.username || "unknown"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={m.role ?? "viewer"}
                          onValueChange={(v) => handleRoleChange(m.user_id!, v as MemberRole)}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="viewer">Viewer</SelectItem>
                            <SelectItem value="commenter">Commenter</SelectItem>
                            <SelectItem value="editor">Editor</SelectItem>
                            <SelectItem value="owner">Owner</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="outline" onClick={() => handleRemove(m.user_id!)}>
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 pt-6 border-t">
                <h4 className="font-semibold mb-3">Pending invites</h4>
                {invitesLoading ? (
                  <div className="text-sm text-muted-foreground">Loading invites…</div>
                ) : (
                  <div className="divide-y rounded-md border">
                    {invites.length === 0 && (
                      <div className="p-3 text-sm text-muted-foreground">No pending invites.</div>
                    )}
                    {invites.map((invite) => {
                      const inviteUrl = `${window.location.origin}/invite/${invite.token}`;
                      return (
                        <div
                          key={invite.id}
                          className="flex items-center justify-between gap-3 p-3"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm truncate">{invite.token}</div>
                            <div className="text-xs text-muted-foreground">
                              Role: {invite.role} • Expires:{" "}
                              {invite.expires_at
                                ? new Date(invite.expires_at).toLocaleDateString()
                                : "Never"}
                            </div>
                            <div className="text-xs text-muted-foreground truncate mt-1">
                              {inviteUrl}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyInviteLink(invite.token)}
                            >
                              Copy link
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteInvite(invite.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}